import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDeviceLogs } from "../../services/API/GetDeviceLogs.jsx";
import { RotateCw } from "lucide-react";
import { LOG_TYPES, LOG_TYPE_MESSAGES } from "../constants.js";

//Controller logs Api Call

import { useUpdateTableNotif } from "../../services/API/ControllerAPI/UpdateLogsTable.jsx";

import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useApiPopup } from "./ApiPopup/ApiPopupProvider.jsx";

const TABLE_TITLES = [
  {
    key: "chipKeyTransaction",
    title: "Valid Key Transactions",
    type: LOG_TYPES.validKey,
  },
  {
    key: "invalidKeyTransaction",
    title: "Invalid Key Transactions",
    type: LOG_TYPES.invalidKey,
  },
  {
    key: "intrusionAlarms",
    title: "Intrusion Alarm Activations",
    type: LOG_TYPES.intrusion,
  },
  {
    key: "openDoorWarning",
    title: "Open Door Warning Activations",
    type: LOG_TYPES.openDoorWarning,
  },

  {
    key: "doorEvents",
    title: "Door Events",
    type: LOG_TYPES.doorEvent,
  },
];

/**
 * Formats the log timestamp in the Europe/Paris time zone.
 */
const dateFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/Paris",
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

/**
 * Formats the log timestamp in the Europe/Paris time zone.
 */
const formatTs = (iso) => {
  if (!iso) return "-";

  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return iso;
  }

  return dateFormatter.format(date);
};

function LogsTable({ title, rows, type }) {
  // Independent pagination per table
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, [rows]);

  const columns = useMemo(
    () =>
      [
        {
          header: "Timestamp",
          accessorKey: "timestamp",
          cell: (info) => formatTs(info.getValue()),
        },
        {
          header: "Description",
          accessorKey: "logType",
          cell: (info) => {
            // Convert the received log type to a number.
            const logType = Number(info.getValue());

            // Return the mapped message or a fallback message.
            return LOG_TYPE_MESSAGES[logType] ?? `Unknown log type: ${logType}`;
          },
        },

        type === LOG_TYPES.doorEvent
          ? {
              // Unique column identifier.
              id: "forcedEntry",

              header: "Forced Entry",
              accessorKey: "byteParam3",
              cell: (info) => {
                const tensionNumber = Number(info.getValue());

                // Show doors from D1 to D8.
                if (tensionNumber > 2) {
                  return "Yes";
                }
                // Return a fallback value when there is no valid door.
                return "No";
              },
            }
          : null,

        type === LOG_TYPES.doorEvent ||
        type === LOG_TYPES.intrusion ||
        type === LOG_TYPES.openDoorWarning
          ? null
          : {
              header: "Key Serial No.",
              accessorKey: "keySerialNumber",
              cell: (info) => info.getValue() ?? "-",
            },

        type === LOG_TYPES.invalidKey
          ? null
          : {
              header: "Door No.",

              // Valid key transactions store the door number in byteParam1.
              // Other log categories store the door number in byteParam2.
              accessorKey:
                type === LOG_TYPES.validKey ? "byteParam1" : "byteParam2",

              cell: (info) => {
                // Convert the received value to a number.
                const doorNumber = Number(info.getValue());

                // Display only valid door numbers.
                if (doorNumber >= 1 && doorNumber <= 8) {
                  return `Door: ${doorNumber}`;
                }

                return "-";
              },
            },

        type === LOG_TYPES.doorEvent
          ? {
              // Unique column identifier.
              id: "doorForce",

              header: "Door Force",
              accessorKey: "byteParam3",
              cell: (info) => {
                const doorForceType = Number(info.getValue());

                const doorForceTypes = {
                  0: { label: "No force", color: "text-green-600" },
                  1: { label: "Light", color: "text-green-600" },
                  2: { label: "Normal", color: "text-green-600" },
                  3: { label: "Strong", color: "text-red-600" },
                  4: { label: "Excessive", color: "text-red-600" },
                };

                const force = doorForceTypes[doorForceType];

                return force ? (
                  <span className={force.color}>{force.label}</span>
                ) : (
                  <span className="text-gray-400">Unknown</span>
                );
              },
            }
          : null,
      ].filter(Boolean),
    [type],
  );

  const table = useReactTable({
    data: rows ?? [],
    columns,
    state: { pagination },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="border border-stone-200 rounded-lg bg-white flex flex-col max-h-[420px]">
      {/* Title bar */}
      <div className="px-4 py-3 border-b border-stone-200 bg-stone-50 flex items-center justify-between gap-3">
        <div className="text-sm font-semibold text-stone-800">{title}</div>
        <div className="text-xs text-stone-600">
          {rows?.length ?? 0} log{(rows?.length ?? 0) === 1 ? "" : "s"}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 w-full overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-white sticky top-0 z-10">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="border-b border-stone-200">
                {hg.headers.map((header) => (
                  <th
                    key={header.id}
                    className="text-left font-semibold text-stone-700 px-3 py-2 whitespace-normal break-words"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-6 text-center text-stone-500"
                >
                  No logs for this category.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-stone-100 hover:bg-stone-50"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-3 py-2 text-stone-700 whitespace-normal break-words"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="text-xs text-stone-600">
          Page {table.getState().pagination.pageIndex + 1} of{" "}
          {table.getPageCount() || 1}
        </div>

        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1.5 text-xs border border-stone-200 rounded hover:bg-stone-50 disabled:opacity-50"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            First
          </button>
          <button
            className="px-3 py-1.5 text-xs border border-stone-200 rounded hover:bg-stone-50 disabled:opacity-50"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Prev
          </button>
          <button
            className="px-3 py-1.5 text-xs border border-stone-200 rounded hover:bg-stone-50 disabled:opacity-50"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </button>
          <button
            className="px-3 py-1.5 text-xs border border-[stone-200] rounded hover:bg-stone-50 disabled:opacity-50"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            Last
          </button>

          <select
            className="ml-2 text-xs border border-stone-200 rounded px-2 py-1.5"
            value={table.getState().pagination.pageSize}
            onChange={(e) => table.setPageSize(Number(e.target.value))}
          >
            {[5, 10, 20, 50].map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

export default function MarketingMenu({
  isOpen,
  onClose,
  onCloseAll,
  controller,
}) {
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Use the available controller identifier.
  const deviceId = controller?.id ?? controller?.device_id ?? null;

  const { mutate: updateLogs, isPending: updateLogsLoading } =
    useUpdateTableNotif();

  const {
    data: marketingControllerData = [],
    error: marketingControllerError,
    refetch: refetchDeviceLogs,
  } = useDeviceLogs(deviceId);

  const popup = useApiPopup();

  // Store the currently selected log category
  const [selectedCategory, setSelectedCategory] = useState("all");

  // Store the Key Serial Number search text
  const [keySerialSearch, setKeySerialSearch] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setSelectedCategory("all");
      setKeySerialSearch("");
    }
  }, [isOpen]);

  const clearKeySerialSearch = () => {
    setKeySerialSearch("");
  };

  //Logs Handling
  const refreshControllerLogs = useCallback(() => {
    if (!deviceId || updateLogsLoading) return;

    updateLogs([deviceId], {
      onSuccess: () => {
        refetchDeviceLogs();
        popup.success("Controller Logs Updated.");
      },
      onError: (error) => {
        popup.error(
          `Failed to update logs: ${error?.message ?? "Unknown error"}`,
        );
      },
    });
  }, [deviceId, updateLogsLoading, updateLogs, refetchDeviceLogs, popup]);

  // Build { chipKeyTransaction: [...], invalidKeyTransaction: [...], ... }
  const groupedLogs = useMemo(() => {
    // Normalize the search text
    const searchValue = keySerialSearch.trim().toLowerCase();

    // Filter rows and sort from newest to oldest
    const filterRows = (rows) => {
      const sortedRows = [...rows].sort(
        (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
      );

      if (!searchValue) {
        return sortedRows;
      }

      return sortedRows.filter((log) =>
        String(log?.keySerialNumber ?? "")
          .toLowerCase()
          .includes(searchValue),
      );
    };

    // Keep only door events with force levels from 1 to 4.
    const filterDoorEvents = (rows) => {
      const forceEvents = rows.filter((log) => {
        // Convert byteParam3 to a numeric force level.
        const forceLevel = Number(log?.byteParam3);

        // Ignore level 0 and invalid values.
        return forceLevel >= 1 && forceLevel <= 4;
      });

      // Apply the existing sorting and search logic.
      return filterRows(forceEvents);
    };

    // Read and filter each category from the API response
    return {
      chipKeyTransaction: filterRows(
        marketingControllerData?.chipKeyTransaction?.data ?? [],
      ),

      invalidKeyTransaction: filterRows(
        marketingControllerData?.invalidKeyTransaction?.data ?? [],
      ),

      intrusionAlarms: filterRows(
        marketingControllerData?.intrusionAlarms?.data ?? [],
      ),

      openDoorWarning: filterRows(
        marketingControllerData?.openDoorWarnings?.data ?? [],
      ),

      doorEvents: filterDoorEvents(
        marketingControllerData?.doorEvents?.data ?? [],
      ),
    };
  }, [marketingControllerData, keySerialSearch]);

  // Return all categories or only the selected category
  const visibleCategories = useMemo(() => {
    if (selectedCategory === "all") {
      return TABLE_TITLES;
    }

    return TABLE_TITLES.filter((category) => category.key === selectedCategory);
  }, [selectedCategory]);

  //   useEffect(() => {
  //     if (marketingControllerLoading) {
  //       popup.pending("Loading controller logs...");
  //     }
  //   }, [marketingControllerLoading, popup]);

  useEffect(() => {
    if (marketingControllerError) {
      popup.error("Device Connection not found...");
    }
  }, [marketingControllerError, popup]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Card */}
      <div className="relative w-full max-w-6xl mx-4 bg-white rounded-xl shadow-lg border border-stone-200 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-stone-200">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            {/* Controller information */}
            <div>
              <div className="text-base font-semibold text-stone-800">
                Marketing Logs
              </div>

              <div className="text-sm text-stone-600">
                {controller?.device_info?.ssid}
              </div>

              <div className="text-sm text-stone-600">
                {controller?.id ?? controller?.device_id ?? "-"}
              </div>
            </div>

            {/* Log filters and refresh action */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full sm:w-65 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-200"
              >
                <option value="all">All Categories</option>

                {TABLE_TITLES.map((category) => (
                  <option key={category.key} value={category.key}>
                    {category.title}
                  </option>
                ))}
              </select>

              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={keySerialSearch}
                  onChange={(e) => setKeySerialSearch(e.target.value)}
                  placeholder="Search Key Serial No."
                  className="w-full sm:w-56 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-200"
                />

                <button
                  type="button"
                  onClick={clearKeySerialSearch}
                  disabled={!keySerialSearch}
                  className="px-3 py-2 rounded-lg border border-stone-300 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear
                </button>
              </div>

              <button
                onClick={refreshControllerLogs}
                disabled={updateLogsLoading || !deviceId}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-700 hover:border-stone-300 hover:bg-stone-50 focus:outline-none focus:ring-2 focus:ring-stone-200 disabled:opacity-50"
                title="Refresh"
              >
                <RotateCw
                  size={16}
                  className={`text-stone-500 ${
                    updateLogsLoading ? "animate-spin" : ""
                  }`}
                />
                Update Logs
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4 flex-1 overflow-y-auto">
          <div className="flex flex-col gap-4">
            {visibleCategories.map((t) => (
              <LogsTable
                key={t.key}
                title={t.title}
                rows={groupedLogs[t.key] ?? []}
                type={t.type}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-stone-200 justify-end flex flex-row gap-4 lg:flex-row">
          {/* Back */}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50 text-sm font-semibold"
          >
            Back
          </button>

          {/* Close */}
          <button
            type="button"
            onClick={onCloseAll || onClose}
            className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 text-sm font-semibold"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
